import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../environments/test.environment';
import { Observable } from 'rxjs';
import { CreateRequestDto } from '../create-request/create-request.component';

export interface Request {
  requestGuid: number;
  projectNumber: number;
  number: string;
  description: string;
  status: string;
  stage: string | null;
  createdDate: string | null;
  requestType: string;
  area: string | null;
  sequence: string | null;
  priority: string;
}

export interface FileUploadResponse {
  fileId: number;
  fileName: string;
  requestGuid: number;
}

export interface FileEntry {
  fileName: any;
  fileId: string;
  uploadedForId: string;
  requestGuid: string;
  fileUrl: string;
  createdDate: string;
  type: string;
  uploadedBy: string;
}

export interface Revision {
  revisionGuid: string;
  revisionNumber?: number | null;
  createdBy?: string | null;
  dateCreated?: string | null;
  questionText?: string | null;
  sentDate?: string | null;
  // optional responses array if backend returns them together
  responses?: ResponseEntry[];
}

export interface ResponseEntry {
  responseGuid: string;
  responder?: string | null;
  dateCreated?: string | null;
  responseText?: string | null;
  dateReceived?: string | null;
}

@Injectable({ providedIn: 'root' })
export class RequestService {
  constructor(private http: HttpClient) {}

  getUnansweredRequests(): Observable<Request[]> {
    return this.http.get<Request[]>(`${environment.apiBaseUrl}/api/requests/unanswered`);
  }

  getAllRequests(filters?: {rfi: boolean, cdq: boolean, crfi: boolean, iq: boolean, risk: boolean}): Observable<Request[]> {
    let params = new HttpParams();
    if (filters) {
      params = params
        .set('rfi', filters.rfi.toString())
        .set('cdq', filters.cdq.toString())
        .set('crfi', filters.crfi.toString())
        .set('iq', filters.iq.toString())
        .set('risks', filters.risk.toString());
    }
    return this.http.get<Request[]>(`${environment.apiBaseUrl}/api/requests`, { params });
  }

  getRequestsByProject(projectNumber: number, filters?: {rfi: boolean, cdq: boolean, crfi: boolean, iq: boolean, risk: boolean}): Observable<Request[]> {
  let params = new HttpParams();
  
  if (filters) {
    params = params
      .set('rfi', filters.rfi.toString())
      .set('cdq', filters.cdq.toString())
      .set('crfi', filters.crfi.toString())
      .set('iq', filters.iq.toString())
      .set('risks', filters.risk.toString());
  }
  
  return this.http.get<Request[]>(
    `${environment.apiBaseUrl}/api/requests/by-project/${projectNumber}`,
    { params }
  );
}

  createRequest(request: CreateRequestDto) : Observable<Request> {
    return this.http.post<Request>(`${environment.apiBaseUrl}/api/requests/create`, request);
  }

  createRevision(requestGuid: string, description: string, createdBy: string, files?: File[], revisionNumber?: number): Observable<any> {
    const formData = new FormData();
    formData.append('RequestGuid', requestGuid);
    formData.append('Description', description);
    formData.append('CreatedBy', createdBy);
    if (revisionNumber !== undefined && revisionNumber !== null) {
      formData.append('RevisionNumber', revisionNumber.toString());
    }
    if (files) {
      for (const file of files) {
        formData.append('Files', file);
      }
    }
    return this.http.post<any>(`${environment.apiBaseUrl}/api/revisions`, formData);
  }

  uploadFile(requestGuid: string, file: File, uploadedBy: string): Observable<FileUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('requestGuid', requestGuid);
  formData.append('uploadedBy', uploadedBy);

  return this.http.post<FileUploadResponse>(`${environment.apiBaseUrl}/api/requests/upload`, formData);
}

getFilesByRequest(requestGuid: string): Observable<FileEntry[]> {
  return this.http.get<FileEntry[]>(`${environment.apiBaseUrl}/api/requests/files/by-request/${requestGuid}`);
}

// Fetch revisions (and optionally their responses) for a given request GUID.
// Backend should return an array of Revision objects; if responses are included
// they should be available on the `responses` property of each revision.
getRevisionsByRequest(requestGuid: string, includeResponses: boolean = false): Observable<Revision[]> {
  let params = new HttpParams();
  if (includeResponses) {
    params = params.set('includeResponses', 'true');
  }
  return this.http.get<Revision[]>(`${environment.apiBaseUrl}/api/requests/${requestGuid}/revisions`, { params });
}

// Fallback: fetch responses for a specific revision if the revisions endpoint
// does not include responses.
getResponsesByRevision(revisionGuid: string): Observable<ResponseEntry[]> {
  return this.http.get<ResponseEntry[]>(`${environment.apiBaseUrl}/api/revisions/${revisionGuid}/responses`);
}

// Fetch files attached to a specific revision
getFilesByRevision(revisionGuid: string): Observable<FileEntry[]> {
  return this.http.get<FileEntry[]>(`${environment.apiBaseUrl}/api/revisions/${revisionGuid}/files`);
}

// Fetch files attached to a specific response
getFilesByResponse(revisionGuid: string, responseGuid: string): Observable<FileEntry[]> {
  return this.http.get<FileEntry[]>(`${environment.apiBaseUrl}/api/revisions/${revisionGuid}/responses/${responseGuid}/files`);
}

// Get the download URL for a file
getFileDownloadUrl(fileId: string): string {
  return `${environment.apiBaseUrl}/api/files/${fileId}`;
}

downloadFile(fileId: string): Observable<Blob> {
  return this.http.get(`${environment.apiBaseUrl}/api/files/${fileId}`, { responseType: 'blob' });
}

getNextRequestNumber(projectNumber: number): Observable<string> {
  return this.http.get(`${environment.apiBaseUrl}/api/requests/next-number/${projectNumber}`, { responseType: 'text' });
}

updateRequestStatus(requestGuid: string, status: string, updatedBy: string, number: string, projectNumber: number): Observable<any> {
  const url = `${environment.apiBaseUrl}/api/requests/update-status/${requestGuid}`;
  const body = { status, updatedBy, number, projectNumber };
  return this.http.post(url, body, {
    headers: { 'Content-Type': 'application/json' }
  });
}

updateRequestStage(requestGuid: string, stage: string | null, updatedBy: string, number: string, projectNumber: number): Observable<any> {
  const url = `${environment.apiBaseUrl}/api/requests/update-stage/${requestGuid}`;
  const body = { stage, updatedBy, number, projectNumber };
  return this.http.post(url, body, {
    headers: { 'Content-Type': 'application/json' }
  });
}

updateRequestPriority(requestGuid: string, priority: string, updatedBy: string, number: string, projectNumber: number): Observable<any> {
  const url = `${environment.apiBaseUrl}/api/requests/update-priority/${requestGuid}`;
  const body = { priority, updatedBy, number, projectNumber };
  return this.http.post(url, body, {
    headers: { 'Content-Type': 'application/json' }
  });
}


}
